import { localMessagingStore, OUTBOX_TTL } from './localMessagingStore';
import { publishOptimisticMessaging } from './optimisticMessagingEvents';
import { isClientReloadRequired } from '../../utils/clientReload';
import { registerSessionReset, sessionIsCurrent, sessionTicket } from '../../utils/sessionLifecycle';

export const AUTO_SEND_AGE = 3600000;
export function mayAutoSend(record, now = Date.now()) {
  return ['text', 'template', 'image', 'video', 'document', 'audio'].includes(record.kind) && record.state === 'waiting' && now - record.queued_at < AUTO_SEND_AGE;
}
export function createOutboxCoordinator({ store = localMessagingStore, publish = publishOptimisticMessaging,
  locks = navigator.locks, channelFactory = name => typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(name), now = Date.now } = {}) {
  let actor = null, generation = 0, connection = 'online', transport, channel, rows = [], listeners = new Set(), expired = [], loading, ready = false;
  const notify = () => listeners.forEach(listener => listener());
  const event = (type, record, extra = {}) => publish({ type, ...record.details, userId: record.user_id, queuedAt: new Date(record.queued_at).toISOString(), ...extra });
  const changed = () => { notify(); channel?.postMessage({ actor, type: 'changed' }); };
  let credentials;
  const current = (owner, ticket) => owner === actor && ticket === generation && sessionIsCurrent(credentials);
  async function restore() {
    const owner = actor, ticket = generation;
    if (owner == null) return;
    const saved = await store.list(owner, 'outbox');
    if (!current(owner, ticket)) return;
    expired = [...expired, ...saved.filter(row => now() - row.queued_at >= OUTBOX_TTL)].filter((row, index, values) => values.findIndex(item => item.id === row.id) === index);
    await Promise.all(expired.map(row => store.remove(owner, 'outbox', row.id)));
    if (!current(owner, ticket)) return;
    rows.filter(row => !saved.some(item => item.id === row.id)).forEach(row => event('discarded', row));
    rows = saved.filter(row => now() - row.queued_at < OUTBOX_TTL).sort((a, b) => a.queued_at - b.queued_at || a.sequence - b.sequence);
    rows.forEach(row => { event('start', row); event('queued', row, { queueState: row.state === 'failed' ? 'failed' : mayAutoSend(row, now()) ? 'waiting' : 'manual', error: row.last_error, errorCode: row.last_error_code, retryAction: row.retry_action }); });
    ready = true; notify();
  }
  return {
    setTransport(value) { transport = value; },
    connection(value) { connection = value; },
    async session(owner) {
      if (owner === actor) return loading;
      const ticket = ++generation; actor = owner; credentials = sessionTicket(); rows = []; expired = []; ready = false;
      channel?.close(); channel = null; notify();
      if (owner == null || !current(owner, ticket)) return;
      channel = channelFactory(`srm-local-${owner}`);
      if (channel) channel.onmessage = message => {
        if (message.data?.actor !== actor) return;
        if (message.data.type === 'accepted') event('accepted', message.data.record, { resource: message.data.resource });
        if (['changed', 'accepted'].includes(message.data.type)) restore().catch(() => {});
      };
      loading = restore(); return loading;
    },
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    snapshot: () => ({ rows, expired, actor, ready }),
    hasPending: conversationId => rows.some(row => row.conversation_id === conversationId),
    offline: () => (connection !== 'online' && connection !== 'restored') || navigator.onLine === false,
    async queue(details, request) {
      if (actor == null) throw new Error('Sign in before queuing a message.');
      const owner = actor, ticket = generation;
      await loading;
      if (!current(owner, ticket)) throw new Error('Your session changed. Message was not queued.');
      const existing = await store.get(owner, 'outbox', details.clientMessageId);
      if (existing && JSON.stringify(existing.request) !== JSON.stringify(request)) throw new Error('A pending message ID cannot be reused with different content.');
      const record = existing || { user_id: owner, store_kind: 'outbox', id: details.clientMessageId,
        conversation_id: details.conversationId, client_message_id: details.clientMessageId, kind: details.kind,
        request, details, queued_at: now(), sequence: rows.length, attempts: 0, last_error: null, state: 'waiting' };
      await store.put(record);
      if (!current(owner, ticket)) throw new Error('Your session changed. Your message remains saved for its original user.');
      rows = [...rows.filter(row => row.id !== record.id), record];
      event('queued', record, { queueState: mayAutoSend(record, now()) ? 'waiting' : 'manual' }); changed();
      return { success: true, queued: true };
    },
    async flush({ id = null } = {}) {
      if (isClientReloadRequired() || actor == null || this.offline() || !locks?.request || !transport) return false;
      const owner = actor, ticket = generation;
      await loading;
      return locks.request(`srm-outbox-${owner}`, async () => {
        if (!current(owner, ticket) || this.offline()) return false;
        const records = (await store.list(owner, 'outbox')).sort((a, b) => a.queued_at - b.queued_at || a.sequence - b.sequence);
        const blocked = new Set();
        for (const record of records) {
          if (isClientReloadRequired() || !current(owner, ticket) || this.offline()) break;
          if (now() - record.queued_at >= OUTBOX_TTL) { await store.remove(owner, 'outbox', record.id); expired.push(record); continue; }
          const manual = record.id === id;
          if (record.retry_action === 'never') { blocked.add(record.conversation_id); continue; }
          if (blocked.has(record.conversation_id) || (!manual && !mayAutoSend(record, now()))) { blocked.add(record.conversation_id); continue; }
          record.attempts++; await store.put(record);
          if (!current(owner, ticket)) break;
          event('queued', record, { queueState: 'sending' });
          try {
            const result = await transport(record);
            if (!current(owner, ticket)) break;
            if (result?.data?.message?.client_message_id !== record.client_message_id) throw new Error('Send acceptance could not be correlated. Keep the original message ID.');
            event('accepted', record, { resource: result.data.message });
            await store.remove(owner, 'outbox', record.id);
            channel?.postMessage({ actor, type: 'accepted', record: { ...record, request: undefined }, resource: result.data.message });
          } catch (error) {
            if (!current(owner, ticket)) break;
            record.state = error.networkFailure ? 'waiting' : 'failed';
            record.last_error = error.message || 'Could not send this message.'; record.last_error_code = error.code || null; record.retry_action = error.retryAction || null; await store.put(record);
            event('queued', record, { queueState: record.state, error: record.last_error, errorCode: record.last_error_code, retryAction: record.retry_action }); blocked.add(record.conversation_id);
          }
        }
        if (current(owner, ticket)) { await restore(); changed(); }
        return true;
      });
    },
    async discard(id) {
      const owner = actor, ticket = generation;
      const row = rows.find(item => item.id === id); if (!row) return;
      await store.remove(owner, 'outbox', id);
      if (!current(owner, ticket)) return;
      event('discarded', row);
      rows = rows.filter(item => item.id !== id); changed();
    },
    dismissExpired(conversationId) { expired = expired.filter(row => row.conversation_id !== conversationId); notify(); },
    dispose() { generation++; channel?.close(); channel = null; listeners.clear(); },
  };
}
export const messagingOutbox = createOutboxCoordinator();
registerSessionReset(() => messagingOutbox.session(null));
